import type { ChainCallRecord } from "@passio/easyai-client";

export type ChainCodeLang = "curl" | "ts" | "fetch" | "python";

export const CHAIN_CODE_LANGS: { value: ChainCodeLang; label: string }[] = [
  { value: "curl", label: "cURL" },
  { value: "ts", label: "TypeScript client" },
  { value: "fetch", label: "fetch" },
  { value: "python", label: "Python" },
];

const shq = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

function truncateDataUris(v: unknown): unknown {
  if (typeof v === "string") return v.startsWith("data:") && v.length > 120 ? `${v.slice(0, 60)}…(${v.length} chars)` : v;
  if (Array.isArray(v)) return v.map(truncateDataUris);
  if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, truncateDataUris(x)]));
  return v;
}

const indentTail = (s: string, pad: string) => s.replace(/\n/g, `\n${pad}`);

/** Python literal for JSON data (`True`/`False`/`None`; JSON string escapes are valid Python). */
export function pyLiteral(v: unknown, indent = ""): string {
  if (v === null || v === undefined) return "None";
  if (typeof v === "boolean") return v ? "True" : "False";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "None";
  if (typeof v === "string") return JSON.stringify(v);
  const inner = `${indent}    `;
  if (Array.isArray(v)) return v.length ? `[\n${v.map((x) => `${inner}${pyLiteral(x, inner)},`).join("\n")}\n${indent}]` : "[]";
  const entries = Object.entries(v as Record<string, unknown>);
  return entries.length ? `{\n${entries.map(([k, x]) => `${inner}${JSON.stringify(k)}: ${pyLiteral(x, inner)},`).join("\n")}\n${indent}}` : "{}";
}

interface Parts {
  base: string;
  chainId: string;
  executionId: string | undefined;
  input: Record<string, unknown>;
  sink: string | undefined;
  label: string;
}

function parts(r: ChainCallRecord, origin: string): Parts {
  return {
    base: origin.replace(/\/+$/, ""),
    chainId: r.chainId,
    executionId: r.executionId,
    input: truncateDataUris(r.requestBody.input_data) as Record<string, unknown>,
    sink: r.sinkNodeId,
    label: r.label ?? r.chainKey ?? "chain",
  };
}

function curl(p: Parts): string {
  const sink = p.sink ? `node_results["${p.sink}"].output` : "the last entry in node_results (its output)";
  return [
    `# 1. Start the run. It returns at once with an execution id.`,
    [
      `curl -X POST ${shq(`${p.base}/api/v2/chains/${p.chainId}/execute`)}`,
      `  -H "Authorization: Bearer $EASYAI_API_KEY"`,
      `  -H 'Content-Type: application/json'`,
      `  -d ${shq(JSON.stringify({ input_data: p.input }))}`,
    ].join(" \\\n"),
    ``,
    `# 2. Poll every 1-2 s until status is completed, failed or cancelled.`,
    [
      `curl ${p.executionId ? shq(`${p.base}/api/v2/chains/executions/${p.executionId}`) : `"${p.base}/api/v2/chains/executions/$EXECUTION_ID"`}`,
      `  -H "Authorization: Bearer $EASYAI_API_KEY"`,
    ].join(" \\\n"),
    ``,
    `# 3. The result is ${sink} (a JSON string or object).`,
    `#    Tokens used: total_tokens, or the sum of each step's tokenUsage.`,
  ].join("\n");
}

function typescript(p: Parts): string {
  return `import { EasyAIClient, executionTokens } from "@passio/easyai-client";

// Server-side only: the EasyAI key must never ship in a browser or app bundle.
const easyai = new EasyAIClient({ baseUrl: "${p.base}", apiKey: process.env.EASYAI_API_KEY! });

// ${p.label}: execute, poll every 1.5 s, then parse the ${p.sink ? `${p.sink} output` : "last step's output"}.
const { execution, output } = await easyai.run(
  "${p.chainId}",
  ${indentTail(JSON.stringify(p.input, null, 2), "  ")},
  {${p.sink ? ` sinkNodeId: "${p.sink}",` : ""} onProgress: (p) => console.log(p.status, \`\${p.polls} polls\`) },
);
console.log(execution.id, executionTokens(execution), output);`;
}

function fetchCode(p: Parts): string {
  const pick = p.sink ? `execution.node_results[${JSON.stringify(p.sink)}]` : "Object.values(execution.node_results).at(-1)";
  return `// Server-side only: the EasyAI key must never ship in a browser or app bundle.
const BASE = "${p.base}";
const headers = { Authorization: \`Bearer \${process.env.EASYAI_API_KEY}\`, "Content-Type": "application/json" };

// 1. Start the run (${p.label}). It returns at once with an execution id.
const startRes = await fetch(\`\${BASE}/api/v2/chains/${p.chainId}/execute\`, {
  method: "POST",
  headers,
  body: JSON.stringify({
    input_data: ${indentTail(JSON.stringify(p.input, null, 2), "    ")},
  }),
});
if (!startRes.ok) throw new Error(\`execute failed: \${startRes.status} \${await startRes.text()}\`);
const started = await startRes.json();

// 2. Poll until the execution finishes.
let execution;
for (let i = 0; i < 240; i++) {
  await new Promise((r) => setTimeout(r, 1500));
  execution = await fetch(\`\${BASE}/api/v2/chains/executions/\${started.id}\`, { headers }).then((r) => r.json());
  if (["completed", "failed", "cancelled"].includes(execution.status)) break;
}
if (execution?.status !== "completed") throw new Error(execution?.error ?? \`chain \${execution?.status ?? "timed out"}\`);

// 3. Read the result step. Outputs can be a JSON string or an object.
const raw = ${pick}?.output;
let output = raw;
if (typeof raw === "string") {
  try {
    output = JSON.parse(raw);
  } catch {
    // plain-text output
  }
}
console.log(execution.id, execution.total_tokens, output);`;
}

function python(p: Parts): string {
  const pick = p.sink ? `execution["node_results"][${JSON.stringify(p.sink)}]` : `list(execution["node_results"].values())[-1]`;
  return `import json
import os
import time

import requests

# Server-side only: the EasyAI key must never ship in a browser or app bundle.
BASE = "${p.base}"
HEADERS = {"Authorization": f"Bearer {os.environ['EASYAI_API_KEY']}"}

# 1. Start the run (${p.label}). It returns at once with an execution id.
input_data = ${pyLiteral(p.input)}
res = requests.post(
    f"{BASE}/api/v2/chains/${p.chainId}/execute",
    headers=HEADERS,
    json={"input_data": input_data},
    timeout=30,
)
res.raise_for_status()
execution_id = res.json()["id"]

# 2. Poll until the execution finishes.
execution = {}
for _ in range(240):
    time.sleep(1.5)
    execution = requests.get(f"{BASE}/api/v2/chains/executions/{execution_id}", headers=HEADERS, timeout=30).json()
    if execution["status"] in ("completed", "failed", "cancelled"):
        break
if execution.get("status") != "completed":
    raise RuntimeError(execution.get("error") or f"chain {execution.get('status', 'timed out')}")

# 3. Read the result step. Outputs can be a JSON string or an object.
raw = ${pick}.get("output")
output = raw
if isinstance(raw, str):
    try:
        output = json.loads(raw)
    except ValueError:
        pass  # plain-text output
print(execution_id, execution.get("total_tokens"), output)`;
}

interface PassioParts {
  host: string;
  executePath: string;
  pollPath: (id: string) => string;
  executionId: string | undefined;
  planType: string;
  input: Record<string, unknown>;
  label: string;
}

const TOKEN_PATH = "/v2/token-cache/unified/oauth/token/";
const PLAN_NOTE = "meal_plan holds the assembled plan: days, constraints, macro_targets, pdf (and household for Family)";

function passioCurl(p: PassioParts): string {
  const auth = [`  -H "Authorization: Bearer $PASSIO_TOKEN"`, `  -H "Passio-ID: $PASSIO_CUSTOMER_ID"`];
  return [
    `# PASSIO_TOKEN and PASSIO_CUSTOMER_ID are access_token and customer_id from (cache until expires_in):`,
    `#   curl "${p.host}${TOKEN_PATH}$PASSIO_KEY"`,
    ``,
    `# 1. Start the plan. It returns at once with an execution id.`,
    [`curl -X POST ${shq(`${p.host}/v2/products${p.executePath}`)}`, ...auth, `  -H 'Content-Type: application/json'`, `  -d ${shq(JSON.stringify({ input_data: p.input }))}`].join(
      " \\\n",
    ),
    ``,
    `# 2. Poll every 2-3 s until status is completed or failed (plans take a few minutes).`,
    [`curl ${p.executionId ? shq(`${p.host}/v2/products${p.pollPath(p.executionId)}`) : `"${p.host}/v2/products${p.pollPath("$EXECUTION_ID")}"`}`, ...auth].join(" \\\n"),
    ``,
    `# 3. ${PLAN_NOTE}.`,
    `#    Tokens charged: platform_billed_tokens (billed once, on the poll that sees the run finish).`,
  ].join("\n");
}

function passioTypescript(p: PassioParts): string {
  return `import { LicenseKeyAuth, PassioClient } from "@passio/api-client";

const passio = new PassioClient({
  baseUrl: "${p.host}/v2/products",
  auth: new LicenseKeyAuth({ licenseKey: process.env.PASSIO_KEY!, host: "${p.host}" }), // server-side only
});

// ${p.label}: start the plan, poll every 3 s until the run ends, then read meal_plan.
const { data } = await passio.tools.runMealPlan("${p.planType}", ${JSON.stringify(p.input, null, 2)});
if (data.status !== "completed") throw new Error(data.error ?? \`meal plan \${data.status}\`);
console.log(data.id, data.platform_billed_tokens, data.meal_plan);`;
}

function passioFetch(p: PassioParts): string {
  return `// Server-side only. Exchange your license key for a token and cache it until expires_in runs out.
const HOST = "${p.host}";
const auth = await fetch(\`\${HOST}${TOKEN_PATH}\${process.env.PASSIO_KEY}\`).then((r) => r.json());
const headers = { Authorization: \`Bearer \${auth.access_token}\`, "Passio-ID": auth.customer_id, "Content-Type": "application/json" };

// 1. Start the plan (${p.label}). It returns at once with an execution id.
const startRes = await fetch(\`\${HOST}/v2/products${p.executePath}\`, {
  method: "POST",
  headers,
  body: JSON.stringify({
    input_data: ${indentTail(JSON.stringify(p.input, null, 2), "    ")},
  }),
});
if (!startRes.ok) throw new Error(\`start failed: \${startRes.status} \${await startRes.text()}\`);
const started = await startRes.json();

// 2. Poll until the run ends (plans take a few minutes).
let execution;
for (let i = 0; i < 200; i++) {
  await new Promise((r) => setTimeout(r, 3000));
  execution = await fetch(\`\${HOST}/v2/products${p.pollPath("${started.id}")}\`, { headers }).then((r) => r.json());
  if (["completed", "failed", "cancelled"].includes(execution.status)) break;
}
if (execution?.status !== "completed") throw new Error(execution?.error ?? \`meal plan \${execution?.status ?? "timed out"}\`);

// 3. ${PLAN_NOTE}.
console.log(execution.id, execution.platform_billed_tokens, execution.meal_plan);`;
}

function passioPython(p: PassioParts): string {
  return `import os
import time

import requests

HOST = "${p.host}"

# Server-side only. Exchange your license key for a token and cache it until expires_in runs out.
auth = requests.get(f"{HOST}${TOKEN_PATH}{os.environ['PASSIO_KEY']}", timeout=30).json()
HEADERS = {"Authorization": f"Bearer {auth['access_token']}", "Passio-ID": auth["customer_id"]}

# 1. Start the plan (${p.label}). It returns at once with an execution id.
input_data = ${pyLiteral(p.input)}
res = requests.post(f"{HOST}/v2/products${p.executePath}", headers=HEADERS, json={"input_data": input_data}, timeout=30)
res.raise_for_status()
execution_id = res.json()["id"]

# 2. Poll until the run ends (plans take a few minutes).
execution = {}
for _ in range(200):
    time.sleep(3)
    execution = requests.get(f"{HOST}/v2/products${p.pollPath("{execution_id}")}", headers=HEADERS, timeout=30).json()
    if execution["status"] in ("completed", "failed", "cancelled"):
        break
if execution.get("status") != "completed":
    raise RuntimeError(execution.get("error") or f"meal plan {execution.get('status', 'timed out')}")

# 3. ${PLAN_NOTE}.
print(execution_id, execution.get("platform_billed_tokens"), execution.get("meal_plan"))`;
}

function passioParts(r: ChainCallRecord, host: string): PassioParts {
  const template = r.executionPath ?? "/napi/tools/mealPlans/executions/{executionId}";
  return {
    host: host.replace(/\/+$/, ""),
    executePath: r.executePath ?? `/napi/tools/mealPlans/${r.chainId}`,
    pollPath: (id) => template.replace("{executionId}", id),
    executionId: r.executionId,
    planType: r.chainId,
    input: truncateDataUris(r.requestBody.input_data) as Record<string, unknown>,
    label: r.label ?? r.chainKey ?? "Meal plan",
  };
}

/**
 * Code that makes the same chain run directly against `origin`: EasyAI (key from `EASYAI_API_KEY`),
 * or for `api: "passio"` runs the Passio API (license key from `PASSIO_KEY`).
 */
export function chainCode(lang: ChainCodeLang, r: ChainCallRecord, origin: string): string {
  if (r.api === "passio") {
    const p = passioParts(r, origin);
    if (lang === "curl") return passioCurl(p);
    if (lang === "ts") return passioTypescript(p);
    if (lang === "fetch") return passioFetch(p);
    return passioPython(p);
  }
  const p = parts(r, origin);
  if (lang === "curl") return curl(p);
  if (lang === "ts") return typescript(p);
  if (lang === "fetch") return fetchCode(p);
  return python(p);
}
